/**
 * The picker that makes the run-bounded consolidation screen reachable without a link.
 *
 * ch04's `CycleRunPicker` is the pattern, and its argument carries over whole: **no read is
 * issued for a run the URL does not name.** The run comes from the directory the platform
 * publishes (`GET /exit/consolidation/runs`), and the choice is written to the URL as `?runId=`.
 *
 * **The default run (DEC-99, 2026-10-01, `docs/adr/9039`, generalising ADR 6011).** With no run
 * in the URL, the newest RECORDED consolidation run is opened — the first row of the directory,
 * the platform's own order — and written into the URL with `replaceState` (since 2026-10-10 the
 * URL is the only memory). A run the URL names always wins. «Il più recente», the picker's
 * first option, drops the run so the page follows the newest again.
 *
 * It replaced a resolver that defaulted on the wrong key — the newest `as_of` date, read from
 * `/exit/consolidation/doors/latest`. On 2026-09-25 that served an orphaned snapshot from a run
 * that made no decisions over the night's real cycle, because a snapshot is dated by the stock it
 * was measured on and a later run can measure an earlier day. A date answers "as at when", and the
 * screen's question is "which consolidation", so the default is the newest RUN, and each option
 * names both: the stock date the run measured and the moment it was recorded, newest recorded
 * first — the platform's order, not one re-sorted here.
 *
 * Changing the run clears `?optionId=`: an option chosen under one run is not a choice made
 * under another, and carrying it across would open the doors of an option the planner has
 * not looked at in the run they just picked.
 *
 * An empty directory is a finding — no consolidation run has been recorded — and renders as
 * that, not as a picker that looks broken.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listConsolidationRuns } from '@/api/generated';
import type { ConsolidationRunSummary } from '@/api/generated';
import { useApiCollection } from '@/api';
import { formatDate } from '@/lib/format';
import { Badge, Button, Select, Skeleton } from '@/primitives';
import {
  AsyncRegion, ChooseRunState, NEWEST_RUN_OPTION, RunDefaultNote, SCOPE_CHOOSER_CONTROL, ScopeChooser, useRunDefault,
  runWordsFallback, type ChooseRunOption, type UseRunDefault,
} from '@/planning';

import { isSimulatedRun, runPerimeterLabel } from './consolidation-simulate';
import { CONSOLIDATION_OPTION_PARAM, CONSOLIDATION_RUN_ID_PARAM, setExitScopeParam } from './scope';

/** The platform job that writes the consolidation position — ADR 7019 in bata-platform. */
export const POSITION_JOB = 'exit.consolidation_position';

/**
 * The platform job that writes the consolidation DECISIONS and LINES — the run a planner
 * chooses. Its directory row names the position run it is paired with
 * (`position_run_id`), which is where the doors and options are read from; the pairing is
 * the platform's statement (bata-platform#315), never a join made here (ADR 7001).
 */
export const CONSOLIDATION_JOB = 'exit.consolidation_run';

/** The memory scope of a consolidation page (DEC-99): the run names its own market. */
export const CONSOLIDATION_RUN_SCOPE = { kind: 'exit.consolidation' } as const;

/** The consolidation-run directory, newest recorded first. Shared by the picker and the
 *  screen, so both read one cached query rather than two that could disagree. */
export function useConsolidationRuns() {
  return useApiCollection(listConsolidationRuns, { query: { job_name: CONSOLIDATION_JOB } });
}

/**
 * The SIMULATED consolidation runs (ADR 7037, owner ruling 2026-10-14), `simulation: true`. The
 * live directory above never lists them (the platform's default), so the picker offers live runs
 * only; this read is issued only when a URL names a run the live directory does not list.
 */
export function useSimulatedConsolidationRuns(enabled: boolean) {
  return useApiCollection(
    listConsolidationRuns,
    { query: { job_name: CONSOLIDATION_JOB, simulation: true } },
    { enabled },
  );
}

export interface NamedConsolidationRun {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  /** The run, from the live directory, else from the simulated one; `undefined` when neither lists it. */
  readonly run: ConsolidationRunSummary | undefined;
}

/**
 * The run a URL names: looked up in the live directory, and — only when that does not list it — in
 * the simulated one, so a link from a simulation's page (`?runId=<decision_run_id>`) resolves.
 */
export function useNamedConsolidationRun(runId: string | null): NamedConsolidationRun {
  const live = useConsolidationRuns();
  const inLive = runId === null ? undefined : live.data?.find((row) => row.run_id === runId);
  const lookSimulated = runId !== null && live.data !== undefined && inLive === undefined;
  const simulated = useSimulatedConsolidationRuns(lookSimulated);
  if (live.isError) return { isPending: false, isError: true, error: live.error, run: undefined };
  if (live.data === undefined) return { isPending: true, isError: false, error: null, run: undefined };
  if (inLive !== undefined || !lookSimulated) return { isPending: false, isError: false, error: null, run: inLive };
  if (simulated.isError) return { isPending: false, isError: true, error: simulated.error, run: undefined };
  if (simulated.data === undefined) return { isPending: true, isError: false, error: null, run: undefined };
  return { isPending: false, isError: false, error: null, run: simulated.data.find((row) => row.run_id === runId) };
}

/** Write a run chosen FOR the planner: `?runId=`, replacing the history entry, `?optionId=` cleared. */
/**
 * A consolidation run in words — «Consolidamento CO · stock al 9 ott 2026» — from the directory this
 * page already reads; «un’esecuzione precedente» for a run it does not list. Never the id (audit A1).
 */
export function useConsolidationRunWords(): (runId: string | null | undefined) => string {
  const { t, i18n } = useTranslation();
  const runs = useConsolidationRuns();
  return (runId) => {
    const row = runId === null || runId === undefined ? undefined : runs.data?.find((each) => each.run_id === runId);
    return row === undefined
      ? runWordsFallback(t, i18n.language)
      : t('exit.consolidation.runWords', { country: row.country_key, asOf: asOfDay(row.as_of, i18n.language) });
  };
}

function writeChosenRun(runId: string): void {
  setExitScopeParam(CONSOLIDATION_RUN_ID_PARAM, runId, { replace: true, clear: [CONSOLIDATION_OPTION_PARAM] });
}

/** Open a run the planner chose: a history step, `?optionId=` cleared. */
function openRun(runId: string): void {
  setExitScopeParam(CONSOLIDATION_RUN_ID_PARAM, runId, { clear: [CONSOLIDATION_OPTION_PARAM] });
}

/** «Il più recente»: forget the choice and drop `?runId=`, so the page opens on the newest. */
function openNewest(memory: UseRunDefault): void {
  memory.chooseNewest();
  setExitScopeParam(CONSOLIDATION_RUN_ID_PARAM, null, { clear: [CONSOLIDATION_OPTION_PARAM] });
}

/**
 * DEC-99 for a consolidation page. Eligible: every run of the directory, newest recorded first —
 * the platform's order, and the rule the Cockpit's lanes panel already resolves by
 * (`resolveExitRun`). The directory lists recorded runs only, so none is in flight or failed.
 */
export function useConsolidationRunDefault(urlRunId: string | null): UseRunDefault {
  const runs = useConsolidationRuns();
  const offered = runs.isError ? [] : runs.data?.map((row) => row.run_id);
  return useRunDefault({ scope: CONSOLIDATION_RUN_SCOPE, urlRunId, offered, write: writeChosenRun });
}

/**
 * «Scegli un run» for a consolidation page (`docs/adr/9033`): since DEC-99 drawn only when no
 * consolidation run exists (or the directory could not be read) — otherwise the
 * newest run is opened. The runs are the directory this page already reads,
 * described for the shared `ChooseRunState`, newest recorded first. An unpaired run is offered and
 * says so in its state, as the picker's label does. `?optionId=` is cleared with the run (an option
 * chosen under one run is not a choice under another).
 */
export function ChooseConsolidationRun({ objectKey }: {
  /** What the page shows once a run is chosen — "one option's doors". */
  readonly objectKey: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const runs = useConsolidationRuns();
  const memory = useConsolidationRunDefault(null);
  const source = memory.resolution.source;
  if (source === 'pending' || source === 'newest') {
    return <div aria-busy="true"><Skeleton rows={3} /></div>;
  }
  const data = runs.data?.map((row): ChooseRunOption => ({
    runId: row.run_id,
    label: t('exit.consolidation.chooseRun.label', { country: row.country_key, asOf: asOfDay(row.as_of, i18n.language) }),
    market: row.country_key,
    recordedAt: row.recorded_at,
    volume: t('exit.consolidation.chooseRun.volume', { decisions: row.decision_count, lines: row.line_count }),
    state: row.position_run_id === null || row.position_run_id === undefined
      ? t('exit.consolidation.chooseRun.unpaired')
      : null,
  }));
  const choose = (runId: string): void => {
    memory.choose(runId);
    openRun(runId);
  };
  return (
    <ChooseRunState
      objectKey={objectKey}
      runs={{ isPending: runs.isPending, isError: runs.isError, error: runs.error, data, refetch: () => runs.refetch() }}
      onChoose={choose}
    />
  );
}

export interface ConsolidationRunPickerProps {
  /** The run the URL currently names, so the control opens on it. */
  readonly runId: string | null;
}

/**
 * A QUIET consolidation run (bata-platform#782, owner ruling 2026-10-20): its inputs were valid and
 * nothing qualified, so it decided nothing. Since the ruling the platform writes such a run's
 * directory row with `decision_count` 0, so it is the newest run, and the page says «ultimo calcolo
 * <data>: nulla da decidere» rather than showing the previous run as if it were current. A run
 * that decided nothing because of a fault has no row at all, so a zero here is never a fault.
 */
export function isQuietRun(run: ConsolidationRunSummary): boolean {
  return run.job_name === CONSOLIDATION_JOB && run.decision_count === 0;
}

/** The quiet-run message's values: the day the run was computed and the stock day it read. */
export function quietRunValues(run: ConsolidationRunSummary, language: string): { date: string; asOf: string } {
  return { date: asOfDay(run.recorded_at, language), asOf: asOfDay(run.as_of, language) };
}

/** A run's stock date in the planner's format, never the ISO string (audit F). */
function asOfDay(value: string, language: string): string {
  return Number.isNaN(Date.parse(value)) ? value : formatDate(value, language, 'UTC');
}

/** A timestamp for a planner, in the active language; the identifier stays in the value. */
function recordedOn(value: string, language: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString(language);
}

export default function ConsolidationRunPicker({
  runId,
}: ConsolidationRunPickerProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  // Consolidation runs: the run that decided and wrote the lanes. Its row carries the position
  // run it is paired with, so one choice scopes every panel. No `country_key` filter, for
  // `CycleRunPicker`'s reason: the platform scopes the read to the principal's geography, and a
  // filter here could only narrow what a planner may see.
  const runs = useConsolidationRuns();
  const memory = useConsolidationRunDefault(runId);
  const [chosen, setChosen] = useState(runId ?? '');
  // The control follows the URL: a run chosen for the planner, or reached by Back, is shown.
  useEffect(() => { setChosen(runId ?? ''); }, [runId]);

  // A simulated run (ADR 7037) is never in the live directory; a link that names one still opens
  // it, offered in the control and badged, so the planner sees it is not a live run.
  const named = useNamedConsolidationRun(runId);
  const perimeter = named.run === undefined ? null : runPerimeterLabel(named.run);

  const load = (liveRows: readonly ConsolidationRunSummary[]): React.JSX.Element => {
    const namedRun = named.run;
    const rows = namedRun !== undefined && isSimulatedRun(namedRun) && !liveRows.some((row) => row.run_id === namedRun.run_id)
      ? [namedRun, ...liveRows]
      : liveRows;
    const options = [
      // «Il più recente» first (DEC-99): it forgets the planner's choice, so the page follows the newest.
      { value: NEWEST_RUN_OPTION, labelKey: 'runDefault.newestOption' },
      ...rows.map((row) => ({
        value: row.run_id,
        // One catalogue message with its values interpolated — G6 test b forbids assembling it.
        // An unpaired run is offered, and says so in its own label: a planner choosing it should
        // know before choosing that its doors cannot be shown (CycleRunPicker's stale-run rule).
        label: t(isSimulatedRun(row)
          ? 'exit.consolidation.run.optionSimulated'
          : row.position_run_id === null || row.position_run_id === undefined
            ? 'exit.consolidation.run.optionUnpaired'
            : 'exit.consolidation.run.option', {
          country: row.country_key,
          asOf: asOfDay(row.as_of, i18n.language),
          recorded: recordedOn(row.recorded_at, i18n.language),
          decisions: row.decision_count,
          lines: row.line_count,
        }),
      })),
    ];
    const highlighted = rows.find((row) => row.run_id === chosen);
    const newest = chosen === NEWEST_RUN_OPTION;

    const open = (): void => {
      if (newest) {
        openNewest(memory);
        return;
      }
      if (highlighted === undefined) return;
      // A planner's act in the picker: no longer a run chosen for them.
      memory.choose(highlighted.run_id);
      openRun(highlighted.run_id);
    };

    return (
      <>
        {/* One line (ADR 9036): the label is the select's accessible name, not drawn; the help
            is behind the heading's (i). An unpaired run still says so in its own option label. */}
        <div className={SCOPE_CHOOSER_CONTROL}>
          <Select
            labelKey="exit.consolidation.run.run"
            labelHidden
            placeholderKey="exit.consolidation.run.runPlaceholder"
            options={options}
            value={chosen}
            onValueChange={setChosen}
          />
        </div>
        <Button
          variant="primary"
          onClick={open}
          disabled={newest ? false : highlighted === undefined || highlighted.run_id === runId}
        >
          {t('exit.consolidation.run.load')}
        </Button>
        {/* DEC-99: a run the planner did not choose is never silently in force. */}
        {perimeter !== null && (
          <span data-run-perimeter={named.run?.perimeter ?? ''} data-run-simulated={isSimulatedRun(named.run) ? 'true' : 'false'}>
            <Badge labelKey={perimeter.key} values={perimeter.values} />
          </span>
        )}
        <RunDefaultNote memoryKey={memory.key} runId={runId} />
      </>
    );
  };

  return (
    <ScopeChooser
      titleKey="exit.consolidation.run.title"
      aboutKey={['exit.consolidation.run.explainer', 'exit.consolidation.run.runHelp']}
    >
      <AsyncRegion
        query={runs}
        errorTitleKey="exit.consolidation.run.loadFailed"
        emptyTitleKey="exit.consolidation.run.emptyTitle"
        emptyBodyKey="exit.consolidation.run.emptyBody"
        isEmpty={(rows) => rows.length === 0}
        loadingRows={1}
      >
        {load}
      </AsyncRegion>
    </ScopeChooser>
  );
}
